import React, { useEffect, useState } from 'react';
import { Tabs, Card, Typography, Avatar, Button, Modal, Input, Upload, message } from 'antd';
import { UploadOutlined, CalendarOutlined } from '@ant-design/icons';
import axios from 'axios';
import useUserProfile from '../../useUserProfile';
import { loginRequest } from '../../authConfig';
import { useMsal } from '@azure/msal-react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { Carousel } from 'antd'; // Import Carousel from Ant Design

const { TabPane } = Tabs;
const { Text } = Typography;
const { TextArea } = Input;

dayjs.extend(utc);
dayjs.extend(timezone);

interface PeerAnniversary {
  userId: string;
  displayName: string;
  country: string;
  anniversaryDate: string;
  yearsWorked: number;
  displayImage?: string;
}

interface SelfAnniversary {
  userId: string;
  displayName: string;
  nominatorDisplayImage: string;
  nominatorDisplayName: string;
  country: string;
  anniversaryDate: string;
  yearsWorked: number;
}

interface MomentsComponentProps {
  peerAnniversaries: PeerAnniversary[];
  selfAnniversary: SelfAnniversary | null;
}

const MomentsComponent: React.FC<MomentsComponentProps> = ({ peerAnniversaries, selfAnniversary }) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedUser, setSelectedUser] = useState<PeerAnniversary | null>(null);
  const [wishText, setWishText] = useState('');
  const [fileList, setFileList] = useState<any[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { requestProfileData } = useUserProfile();
  const { instance, accounts } = useMsal();
  const [userDetails, setUserDetails] = useState<{ id: string; displayName: string; email: string }[]>([]);
  const [wishes, setWishes] = useState<any[]>([]);
  const [userRoles, setUserRoles] = useState<string[]>([]); // Update to handle multiple roles

  useEffect(() => {
    const fetchProfileData = async () => {
      try {
        await requestProfileData();
        const userMail = localStorage.getItem('UserMail');
        const userName = localStorage.getItem('UserName');
        const userId = localStorage.getItem('UserId');
        setUserDetails([{ id: userId || '', displayName: userName || 'Default Nominator', email: userMail || '' }]);
      } catch (error) {
        console.error('Error fetching profile data:', error);
      }
    };

    fetchProfileData();
  }, [requestProfileData]);

  const fetchToken = async () => {
    const res = await instance.acquireTokenSilent({
      ...loginRequest,
      account: accounts[0],
    });
    return res.idToken;
  };

  const fetchUserRoleAndWishes = async () => {
    try {
      const token = await fetchToken();
      const userId = localStorage.getItem('UserId');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/WorkAnniversaryMilestoneService/user/role-and-wishes`, {
        params: { userId },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setUserRoles(response.data.roles || []); // Ensure roles array is set correctly
      setWishes(response.data.wishes || []);
    } catch (error) {
      console.error('Error fetching user role and wishes:', error);
    }
  };

  useEffect(() => {
    fetchUserRoleAndWishes();
  }, []);

  const showModal = async (user: PeerAnniversary) => {
    setSelectedUser(user);

    try {
      const token = await fetchToken();
      const formattedAnniversaryDate = dayjs(user.anniversaryDate).tz('Asia/Kolkata').format('YYYY-MM-DD HH:mm:ssZ');

      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/WorkAnniversaryMilestoneService/wish/details`, {
        params: {
          nominatorId: userDetails[0]?.id,
          nomineeId: user.userId,
          anniversaryDate: formattedAnniversaryDate,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.data && response.data !== true) {
        setWishText(response.data.description);
        setFileList([
          {
            uid: '-1',
            name: 'Existing Image',
            status: 'done',
            url: `data:image/png;base64,${response.data.image}`, // Ensure Base64 image is set here
          },
        ]);
        setIsSubmitting(true);
      } else {
        setWishText('');
        setFileList([]);
        setIsSubmitting(false);
      }

      setIsModalVisible(true);
    } catch (error) {
      console.error('Error fetching wish details:', error);
    }
  };
  const handleOk = async () => {
    if (!wishText.trim()) {
      message.error('Please write a wish before submitting.');
      return;
    }

    if (fileList.length === 0) {
      message.error('Please upload an image.');
      return;
    }

    const data = new FormData();
    data.append('NominatorId', userDetails[0]?.id);
    data.append('NomineeId', selectedUser?.userId || '');
    data.append('Message', wishText);

    // Convert the uploaded file to a byte array
    const file = fileList[0].originFileObj;
    const imageBuffer = await file.arrayBuffer();
    const byteArray = new Uint8Array(imageBuffer);
    data.append('Image', new Blob([byteArray]));

    setIsSubmitting(true);
    try {
      const token = await fetchToken();
      await axios.post(`${process.env.REACT_APP_API_URL}/api/WorkAnniversaryMilestoneService/wish`, data, {
        headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` },
      });
      message.success('Wish posted successfully!');
      setIsModalVisible(false);
      setWishText('');
      setFileList([]);
    } catch (error) {
      message.error("your wish could not be posted. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleCancel = () => {
    if (wishText || fileList.length > 0) {
      Modal.confirm({
        title: 'Are you sure?',
        content: 'You have unsaved changes. Closing this will discard them.',
        okText: 'Yes, Discard',
        cancelText: 'No, Go Back',
        onOk: () => {
          setIsModalVisible(false);
          setWishText('');
          setFileList([]);
        },
      });
    } else {
      setIsModalVisible(false);
    }
  };

  const handleFileChange = ({ fileList: newFileList }: any) => {
    setFileList(newFileList.slice(-1));
  };

  const renderCard = (data: PeerAnniversary | SelfAnniversary) => (
    <Card
      key={data.userId}
      style={{
        width: 254,
        margin: '10px',
        border: '1px solid #e0e0e0',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        textAlign: 'center',
      }}
    >
      <Avatar
        src={'displayImage' in data && data.displayImage ? `data:image/png;base64,${data.displayImage}` : undefined}
        style={{
          backgroundColor: 'displayImage' in data && data.displayImage ? 'transparent' : getRandomColor(),
          color: '#fff',
          fontSize: '32px',
          marginBottom: '10px',
        }}
        size={64}
      >
        {'displayImage' in data && !data.displayImage && data.displayName.charAt(0).toUpperCase()}
      </Avatar>
      <Text strong style={{ fontSize: '18px', display: 'block', marginBottom: '10px' }}>
        {data.displayName}
      </Text>
      {/* <Text style={{ display: 'block', marginBottom: '5px' }}>Country: {data.country}</Text> */}
      <Text style={{ display: 'block', marginBottom: '5px' }}>
        Anniversary Date: {new Date(data.anniversaryDate).toLocaleDateString()}
      </Text>
      <Text style={{ display: 'block', marginBottom: '5px' }}>
        <CalendarOutlined style={{ marginRight: '5px', color: 'black' }} />
        {data.yearsWorked} Years
      </Text>
      <Button type="primary" style={{ marginTop: '10px', backgroundColor: '#8629A4' }} onClick={() => showModal(data)}>
        Wish Now
      </Button>
    </Card>
  );

  const getRandomColor = () => {
    const letters = '0123456789ABCDEF';
    let color = '#';
    for (let i = 0; i < 6; i++) {
      color += letters[Math.floor(Math.random() * 16)];
    }
    return color;
  };

  return (
    <div style={{ padding: '20px', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
      <Tabs defaultActiveKey={selfAnniversary ? 'self' : 'peer'} type="card">
      {selfAnniversary && wishes.length > 0 ? (
  <TabPane tab="My Wishes" key="self">
    <div style={{ marginTop: '20px' }}>
      {/* <Carousel autoplay dotPosition="bottom" style={{ padding: '44px',width:'54%',margin:'0px auto', backgroundColor: '#f9f9f9', borderRadius: '12px' }}>
  {wishes.map((wish, index) => (
    <div
      key={index}
      style={{
        textAlign: 'center',
        padding: '20px',
        borderRadius: '12px',
        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
        backgroundColor: '#fff',
        margin: '0 auto',
        maxWidth: '400px',
        transition: 'transform 0.3s ease',
      }}
    >
      <Avatar
        src={`data:image/png;base64,${wish.nominatorDisplayImage}`}
        style={{
          backgroundColor: 'transparent',
          color: '#fff',
          fontSize: '32px',
          marginBottom: '10px',
        }}
        size={64}
        />
              <h2>{wish.nominatorDisplayName}</h2>

      <Text
        style={{
          fontSize: '18px',
          fontWeight: 'bold',
          display: 'block',
          marginBottom: '15px',
          color: '#333',
        }}
      >
        {wish.message}
      </Text>
      {wish.image && (
        <img
          src={`data:image/png;base64,${wish.image}`}
          alt="Wish Image"
          style={{
            width: '100%',
            maxWidth: '300px',
            height: 'auto',
            objectFit: 'contain', // Changed from 'cover' to 'contain'
            borderRadius: '12px',
            border: '1px solid #e0e0e0',
            margin: '10px auto',
            transition: 'transform 0.3s ease',
          }}
          onMouseOver={(e) => (e.currentTarget.style.transform = 'scale(1.05)')}
          onMouseOut={(e) => (e.currentTarget.style.transform = 'scale(1)')}
        />
      )}
    </div>
  ))}
</Carousel> */}
<style>
  {`
  .slick-list{
  padding: 20px !important;
  }
  `}
</style>
{/* Redesigned Carousel */}
<Carousel autoplay dotPosition="bottom" style={{ padding: '20px', width: '60%', margin: '0 auto', backgroundColor: '#f9f9f9', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)' }}>
  {wishes.map((wish, index) => (
    <div
      key={index}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '20px',
        borderRadius: '12px',
        backgroundColor: '#fff',
        maxWidth: '500px',
        margin: '0 auto',
        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
        transition: 'transform 0.3s ease',
      }}
    >
      {/* Avatar and Name */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px', width: '100%' }}>
        <Avatar
          src={wish.nominatorDisplayImage ? `data:image/png;base64,${wish.nominatorDisplayImage}` : undefined}
          style={{
            backgroundColor: wish.nominatorDisplayImage ? 'transparent' : '#1890ff', // Default background color for avatar
            color: '#fff',
            fontSize: '32px',
            marginRight: '15px',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
          }}
          size={64}
        >
          {!wish.nominatorDisplayImage && wish.nominatorDisplayName.charAt(0).toUpperCase()} {/* Display initial if no image */}
        </Avatar>
        <h2 style={{ fontSize: '20px', fontWeight: 'bold', color: '#333', margin: 0 }}>
          {wish.nominatorDisplayName} wished your anniversary...
        </h2>
      </div>

      {/* Message */}
      <Text
        style={{
          fontSize: '16px',
          fontWeight: '500',
          textAlign: 'center',
          marginBottom: '20px',
          color: '#555',
          lineHeight: '1.5',
        }}
      >
        {wish.message}
      </Text>

      {/* Image */}
      {wish.image && (
        <img
          src={`data:image/png;base64,${wish.image}`}
          alt="Wish Image"
          style={{
            width: '100%',
            maxWidth: '400px',
            height: 'auto',
            objectFit: 'contain',
            borderRadius: '12px',
            border: '1px solid #e0e0e0',
            margin: '1px auto',
            transition: 'transform 0.3s ease',
          }}
          onMouseOver={(e) => (e.currentTarget.style.transform = 'scale(1.05)')}
          onMouseOut={(e) => (e.currentTarget.style.transform = 'scale(1)')}
        />
      )}
    </div>
  ))}
</Carousel>
    </div>
  </TabPane>
) : (
  <TabPane tab="My Anniversary" key="self">
    <div style={{ textAlign: 'center', marginTop: '20px' }}>
      <Text style={{ fontSize: '16px', color: '#888' }}>No wishes available for your anniversary.</Text>
    </div>
  </TabPane>
)}

        {peerAnniversaries.length > 0 && (
          <TabPane tab="Peer Anniversaries" key="peer">
            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center' }}>
              {peerAnniversaries.map((peer) => renderCard(peer))}
            </div>
          </TabPane>
        )}
      </Tabs>

      <style>{`
        .ant-modal .ant-modal-content {
          padding: 0 !important; 
        }

        .ant-modal .ant-modal-header {
          background-color: #1890ff;
          color: #fff;
          border-radius: 8px 8px 0 0;
        }
          .ant-modal-footer{
          padding: 20px !important;
          }
          .ant-modal-close{
          top:4px !important;
          margon-top: 0px !important;
          }
          .ant-modal-confirm-body-wrapper{
          padding: 20px !important;}
      `}
      </style>

      <Modal
        visible={isModalVisible}
        onOk={!isSubmitting ? handleOk : undefined}
        onCancel={handleCancel}
        okText="Send Wish"
        cancelText="Close"
        confirmLoading={isSubmitting}
        width={800}
        bodyStyle={{
          minHeight: '320px',
          maxHeight: '330px', // Set a maximum height for the modal body
          borderRadius: '8px',
          padding: '20px',
          overflow: 'auto', // Enable scrolling when content exceeds the height
          position: 'relative',
          
        }}        
        title={
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              backgroundColor: '#1890ff',
              color: '#fff',
              padding: '8px 10px',
              borderRadius: '8px 8px 0 0',
            }}
          >
            <span style={{ fontSize: '16px', fontWeight: 'bold' }}>Memories 🎆</span>
          </div>
        }
      >
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px' }}>
          {selectedUser?.displayImage && (
            <Avatar
              src={`data:image/png;base64,${selectedUser.displayImage}`}
              size={64}
              style={{ marginRight: '20px' }}
            />
          )}
          <div>
            <Text strong style={{ fontSize: '16px', display: 'block', marginBottom: '5px' }}>
              {selectedUser?.displayName}
            </Text>
            <Text style={{ fontSize: '14px', color: '#555' }}>
              Years Worked: {selectedUser?.yearsWorked}
            </Text>
          </div>
        </div>
        <Text strong style={{ display: 'flex', alignItems: 'center', marginBottom: '5px' }}>
          <span style={{ color: '#ff4d4f', marginRight: '5px' }}>*</span>
          Write Your Wishes
        </Text>
        <TextArea
          rows={4}
          placeholder="Write your message here..."
          value={wishText}
          onChange={(e) => setWishText(e.target.value)}
          style={{ marginBottom: '10px', borderRadius: '8px', border: '1px solid #d9d9d9', padding: '10px' }}
          disabled={isSubmitting}
        />
        <Upload
          fileList={fileList}
          beforeUpload={(file) => {
            const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png';
            if (!isJpgOrPng) {
              message.error('You can only upload JPG or PNG files!');
            }
            return isJpgOrPng || Upload.LIST_IGNORE;
          }}
          onChange={handleFileChange}
          maxCount={1}
          accept="image/png,image/jpeg"
          listType="picture"
          disabled={isSubmitting}
        >
          <Button icon={<UploadOutlined />} disabled={isSubmitting}>
            Upload Image
          </Button>
        </Upload>
        {fileList.length > 0 && (
          <div style={{ marginTop: '10px', textAlign: 'center', display: 'flex', flexDirection: 'column' }}>
            <Text strong>Uploaded Image:</Text>
            <img
              src={
                fileList[0].url // Use the URL if the file is already uploaded
                  ? fileList[0].url
                  : fileList[0].originFileObj // Use Object URL for newly uploaded files
                    ? URL.createObjectURL(fileList[0].originFileObj)
                    : ''
              }
              alt="Uploaded"
              style={{
                width: '100%',
                maxWidth: '234px',
                marginTop: '10px',
                borderRadius: '8px',
                border: '1px solid #d9d9d9',
                margin: '10px auto',
              }}
              onLoad={(e) => URL.revokeObjectURL((e.target as HTMLImageElement).src)} // Revoke Object URL after loading
            />
          </div>
        )}
      </Modal>
    </div>
  );
};

export default MomentsComponent;